import { Site } from '@envato/foundation-types'
import get from 'lodash-es/get'
import isEmpty from 'lodash-es/isEmpty'
import pick from 'lodash-es/pick'

import sitesConfig from '../../config/sites'
import { IExperimentsState } from '../reducers/experiments'
import { ICommonLocation } from './location/types'

export interface IFeaturesConfig {
  [featureName: string]: boolean
}

export interface IExperimentsToFeaturesConfig {
  [experimentId: string]: {
    [variant: number]: IFeaturesConfig
  }
}

/**
 * Returns `IFeaturesConfig` based on purely `location.query.features` param
 */
export const getLocationFeaturesConfig = (
  featuresConfig: IFeaturesConfig,
  location: ICommonLocation
): IFeaturesConfig => {
  const locationString = get(location, 'query.features', '') as string
  const locationArray = locationString
    .split(',')
    .filter((value: any) => !isEmpty(value))
  const locationConfig = locationArray.reduce<IFeaturesConfig>(
    (config: any, featureName: string) => {
      if (featureName[0] === '!') {
        featureName = featureName.slice(1)
        config[featureName] = false
        return config
      }

      config[featureName] = true
      return config
    },
    {}
  )
  return pick(locationConfig, Object.keys(featuresConfig))
}

/**
 * Returns `IFeaturesConfig` based on purely `features` section from static site config
 * (config/sites/*)
 */
export const getSitesFeaturesConfig = (currentSite: Site): IFeaturesConfig => {
  const defaultConfig = get(sitesConfig, 'defaults.features', {})
  const siteConfig = get(sitesConfig, `${currentSite}.features`, {})

  return {
    ...defaultConfig,
    ...siteConfig
  }
}

/**
 * Returns `IFeaturesConfig` based on `IExperimentsState` from redux store and
 * `IExperimentsToFeaturesConfig` mapping from static site config
 */
export const getExperimentsFeaturesConfig = (
  currentSite: Site,
  activeExperimentsState: IExperimentsState
): IFeaturesConfig => {
  const defaultConfig = get(sitesConfig, 'defaults.experiments_to_features', {})
  const siteConfig = get(
    sitesConfig,
    `${currentSite}.experiments_to_features`,
    {}
  )
  const experimentsToFeaturesConfig: IExperimentsToFeaturesConfig = {
    ...defaultConfig,
    ...siteConfig
  }

  return Object.keys(experimentsToFeaturesConfig).reduce<IFeaturesConfig>(
    (featuresConfig: IFeaturesConfig, experimentId: string) => {
      let variantFeaturesConfig: IFeaturesConfig = {}
      const activeExperimentState = activeExperimentsState[experimentId]

      if (activeExperimentState && typeof activeExperimentState === 'object') {
        const experimentVariant = Number(activeExperimentState.variant)
        const experimentFeaturesConfig =
          experimentsToFeaturesConfig[experimentId] || {}
        variantFeaturesConfig = experimentFeaturesConfig[experimentVariant]
      }
      return {
        ...featuresConfig,
        ...variantFeaturesConfig
      }
    },
    {}
  )
}

export const getFeaturesConfig = (
  currentSite: Site,
  location: ICommonLocation,
  experimentsState: IExperimentsState
) => {
  const mainConfig = getSitesFeaturesConfig(currentSite)
  const experimentsConfig = getExperimentsFeaturesConfig(
    currentSite,
    experimentsState
  )
  const locationConfig = getLocationFeaturesConfig(mainConfig, location)
  return {
    ...mainConfig,
    ...experimentsConfig,
    ...locationConfig
  }
}

export const hasFeature = (
  name: string,
  currentSite: Site,
  location: ICommonLocation,
  experimentsState: IExperimentsState = {}
): boolean => {
  return !!getFeaturesConfig(currentSite, location, experimentsState)[name]
}
